import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { Menu } from '@material-ui/core';
import { SIZES } from 'common/constants/sizes';
import SearchInput from 'components/SearchInput';
import { MenuButtonItem } from 'components/MenuButton';
import { getColor } from 'common/constants/_colors';
import Icon from 'components/base/Icon';
import { LineSkeleton } from './base/Skeletons';
import { Flex } from './base/layout-components';
import Text from 'components/base/Text';
import {
  PM_ENTITIES_NO_DATA_MESSAGE,
  FREE_CHILD_MESSAGE
} from './FilterMenus/FilterMenus.service';
import { formatCypressTag } from 'common/utils/cypress.utils';
import { Stack } from '@mui/material';

const StyledMenuLoaderLine = styled.div`
  width: 85%;
  margin-top: 1.1rem;
`;

const MenuLoader = () => {
  const LINES_COUNTER = 3;
  const LoadingLines = [];
  for (let i = 0; i < LINES_COUNTER; i++)
    LoadingLines.push(
      <StyledMenuLoaderLine key={i}>
        <LineSkeleton size={100} />
      </StyledMenuLoaderLine>
    );

  return (
    <Flex direction='column' items='center' justify='center' width='100%'>
      {LoadingLines}
    </Flex>
  );
};

const DisabledMenuWrapper = styled.div`
  ${({ disabled }) =>
    disabled
      ? `
  background: white;
  opacity: 0.3;
  pointer-events: none;
  `
      : ''};
`;

const StyledMenu = styled(Menu)`
  ${({ margin }) => `margin-left: ${margin};`}
  margin-top: 0.5rem;

  ul {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    padding-right: unset !important;
    width: unset !important;
  }
  .MuiPopover-paper {
    overflow-y: hidden !important;
    ${({ width }) => `width: ${`${width} !important`};`}
  }

  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.3;
  `}
`;

const StyledMenuHeaderItem = styled.li`
  line-height: 2rem !important;
  font-size: ${SIZES.small};
  padding: 1rem 1rem 0.5rem 1rem;
  color: ${getColor('shuttleGray')} !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const StyledMenuSubheaderItem = styled.li`
  font-size: ${SIZES.small};
  padding: 0rem 1rem;
  color: ${getColor('shuttleGray')} !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const StyledMenuItem = styled.li`
  padding: 1rem;
  text-align: end;
`;

const EmptyStateMessage = ({ noDataMessage = {} }) => {
  const { message, extra } = noDataMessage;
  return (
    <Flex items='center' direction='column' pt='small'>
      <Text padding='1rem' size='medium'>
        {message}
      </Text>
      {extra}
    </Flex>
  );
};

const renderMenuItems = ({
  menuItems,
  isMultiple,
  selectedCheckboxes = [],
  maxSelectedCheckboxes
}) =>
  !menuItems.length
    ? []
    : menuItems.map(
        ({
          title,
          id,
          icon,
          onClick = _.noop,
          disabled,
          isSelected = false,
          isActive = false,
          isWithIcon,
          checkboxOnChange = _.noop,
          inactive,
          condition = true,
          style,
          Component,
          extraPayloadPicker = [],
          hoverText,
          ...entity
        }) => {
          if (!condition) return false;
          const extraPayload = _.pick(entity, extraPayloadPicker);

          const selected = _.xorBy(
            selectedCheckboxes,
            [
              {
                id: String(id),
                title,
                ...extraPayload
              }
            ],
            'title'
          );

          const isMaxSelected =
            !!maxSelectedCheckboxes &&
            selectedCheckboxes.length >= maxSelectedCheckboxes;
          const isSelectedItem =
            isSelected ||
            selectedCheckboxes.find(item =>
              item.id ? String(item.id) === String(id) : item.title === title
            );
          const isDisabled = isMaxSelected && !isSelectedItem;

          if (Component)
            return <Component key={id} onClick={onClick} isActive={isActive} />;

          return (
            <li key={id || title} style={style}>
              <MenuButtonItem
                id={id}
                text={title}
                hoverText={hoverText}
                disabled={inactive || isDisabled || disabled}
                isSelected={isSelectedItem}
                isActive={isActive}
                isWithIcon={isWithIcon}
                icon={icon && <Icon name={icon} color='nepal' />}
                isMultiple={isMultiple}
                selectedCheckboxes={selectedCheckboxes}
                data-testid={`filter-item-${id}`}
                data-cy={`filter-item-${id}-data-cy`}
                {...(!isDisabled && {
                  checkboxOnChange: () => {
                    checkboxOnChange(selected);
                  },
                  onClick: () => {
                    if (inactive || isDisabled || disabled) return;
                    onClick();
                    checkboxOnChange(selected);
                  }
                })}
              />
            </li>
          );
        }
      );

const SearchableMenu = ({
  isLevel = 1,
  leftPosition = '-0.3rem',
  isMultiple = false,
  isSearchable = false,
  isDisabled = false,
  isLoading = false,
  id,
  header = '',
  subheader,
  actions,
  menuItems = [],
  freeChild,
  freeChildMessage = FREE_CHILD_MESSAGE,
  width = '15rem',
  selectedCheckboxes,
  maxSelectedCheckboxes,
  anchorEl,
  handleMenusClose = _.noop,
  onSearch,
  searchFilterValue,
  setSearchFilterValue = _.noop,
  selectAllEnabled,
  toggleSelectAll = _.noop,
  noDataMessage = PM_ENTITIES_NO_DATA_MESSAGE,
  onScrollToBottom,
  maxHeight = '33rem'
}) => {
  const [selectAllValue, setSelectAllValue] = useState(false);
  const [localSearchKeyword, setLocalSearchKeyword] = useState('');

  const listRef = useRef(null);

  const handleScroll = () => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 1) {
        onScrollToBottom?.();
      }
    }
  };

  const initSelectAll = () => {
    const isAllSelected = menuItems.every(item => item.isSelected);
    setSelectAllValue(isAllSelected);
  };

  useEffect(() => {
    const currentList = listRef.current;
    if (currentList) {
      currentList.addEventListener('scroll', handleScroll);
    }
    if (selectAllEnabled) initSelectAll();

    return () => {
      if (currentList) {
        currentList.removeEventListener('scroll', handleScroll);
      }
    };
  }, [menuItems]);

  const isLocalSearch = isSearchable && !onSearch;

  const toggleSelectAllValue = () => {
    const newSelectAllValue = !selectAllValue;
    setSelectAllValue(newSelectAllValue);
    toggleSelectAll(newSelectAllValue);
  };

  const handleClose = () => {
    setSearchFilterValue('');
    handleMenusClose();
  };

  const onLocalSearch = value => setLocalSearchKeyword(value);

  useEffect(() => {
    if (selectAllEnabled && selectedCheckboxes?.length === 0) {
      setSelectAllValue(false);
    }
  }, [anchorEl]);

  const filteredMenuItems = isLocalSearch
    ? menuItems.filter(menuItem =>
        menuItem.title?.toLowerCase().includes(localSearchKeyword.toLowerCase())
      )
    : menuItems;

  const hasItems = !!filteredMenuItems.length;
  return (
    <StyledMenu
      id={id}
      margin={leftPosition}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      getContentAnchorEl={null}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      width={width}
      transitionDuration={0}
    >
      <DisabledMenuWrapper
        disabled={isDisabled}
        data-cy={header ? formatCypressTag(header) : 'select-type-box'}
      >
        {hasItems && header && (
          <StyledMenuHeaderItem>{header}</StyledMenuHeaderItem>
        )}
        {hasItems && subheader ? (
          <StyledMenuSubheaderItem>{subheader}</StyledMenuSubheaderItem>
        ) : null}
        {isSearchable && (
          <StyledMenuItem>
            <SearchInput
              value={searchFilterValue}
              placeholder='Search'
              width='unset'
              height='2.6rem'
              autoFocus
              onChange={value =>
                isLocalSearch ? onLocalSearch(value) : onSearch(value)
              }
            />
          </StyledMenuItem>
        )}

        <Stack ref={listRef} sx={{ maxHeight, overflow: 'auto' }}>
          {hasItems && selectAllEnabled && !isLoading && (
            <MenuButtonItem
              text='Select All'
              isSelected={selectAllValue}
              isMultiple={true}
              onClick={toggleSelectAllValue}
              textStyle={{ fontWeight: 'bold' }}
            />
          )}

          {isLoading ? (
            <MenuLoader />
          ) : (
            renderMenuItems({
              menuItems: filteredMenuItems,
              isMultiple,
              selectedCheckboxes,
              maxSelectedCheckboxes
            })
          )}
        </Stack>
        {!hasItems && !isLoading && (
          <EmptyStateMessage
            noDataMessage={freeChild ? freeChildMessage : noDataMessage}
          />
        )}
        {freeChild}
        {actions && <StyledMenuItem>{actions}</StyledMenuItem>}
      </DisabledMenuWrapper>
    </StyledMenu>
  );
};

export default SearchableMenu;
